import { z } from 'zod'

import { globalSchema } from '{alphalib}/types/db/globalSchema.ts'
import { dbAccountSchema } from '{alphalib}/types/db/schema.ts'

export const LoginData = z.object({
  workspace_slugs: z.unknown().array().optional(),
  active_workspace_slug: z.string().optional(),
  app_id: z.string().optional(),
  avatarUrl: z.string(),
  body_classes: z.string().array().optional(),
  cookieVal: z.string().optional(),
  created_epoch: z.number().optional(),
  email: z.string(),
  id: z.string().optional(),
  intercom_hash: z.string().optional(),
  isImpersonated: z.boolean().optional(),
  loggedIn: z.boolean(),
  name: z.string().nullable(),
  plan_slug: z.string(),
})

const isoDateString = z.string().refine((value) => !Number.isNaN(Date.parse(value)), {
  message: 'Invalid ISO date string',
})

export const Workspace = dbAccountSchema.extend({
  last_used: z.union([isoDateString, globalSchema.mysqlDATETIME]).nullable(),
  auto_replay_crashed_assemblies: z.boolean(),
  bill_limit: z.number().nullable(),
  bill_limit_reached: z.boolean(),
  billable: z.boolean(),
  can_be_auto_cancelled: z.boolean(),
  change_billing_email_too: z.boolean(),
  created: z.coerce.date(),
  deleted: z.coerce.date().nullable(),
  lifetime_usage: z.number(),
  modified: z.coerce.date(),
  plan_exceeded: z.boolean(),
  require_template_id: z.boolean(),
  revenue_forecast: z.number(),
  save_template_in_assembly_json: z.boolean(),
  signatureAuth: z.boolean(),
  signup_discount: z.number(),
  stop_processing_on_bill_limit: z.boolean(),
  usage_forecast: z.number(),
  uses_invoicing: z.boolean(),
  cancelled: z.coerce.date().nullable(),
})

export const MembershipWorkspace = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  cancelled: z.coerce.date().nullable(),
})

export const Membership = z.object({
  id: z.string(),
  account_id: z.string(),
  user_id: z.string(),
  role: z.string(),
  app_icon_color: z.string(),
  email_when_close_to_plan_limit: z.number(),
  last_idle_email: z.number(),
  last_idle_email_on: z.coerce.date().nullable(),
  last_close_to_plan_limit_email_on: z.string().nullable(),
  last_bill_limit_reached_email_on: z.string().nullable(),
  last_plan_exceeded_email_on: z.string().nullable(),
  email_on_assembly_errors: z.number(),
  email_on_assembly_warnings: z.number(),
  created: z.coerce.date(),
  modified: z.coerce.date(),
  deleted: z.coerce.date().nullable(),
  workspace: MembershipWorkspace,
})

export const Account = z.object({
  login_data: LoginData,
  workspace: Workspace,
  memberships: Membership.array(),
  ifShowEmailVerificationBanner: z.boolean(),
  ifShowUnpaidInvoicesBanner: z.boolean(),
  ifShowImpersonatedByBanner: z.boolean(),
  isFounder: z.boolean(),
  isAdmin: z.boolean(),
})

export type LoginData = z.infer<typeof LoginData>
export type Workspace = z.infer<typeof Workspace>
export type Account = z.infer<typeof Account>
export type Membership = z.infer<typeof Membership>
export type MembershipWorkspace = z.infer<typeof MembershipWorkspace>
